import style from './style.module.scss';
import React, { LegacyRef, useCallback, useEffect, useRef, useState } from 'react';
import { joinNames } from '../helpers';
import icons from '../../icons/icons.module.scss';
import { ActionButton, ActionPayload, isButtonAction } from '../Button';
import { useFormikContext } from 'formik';
import { valueAt } from '@n2-common/lib/utils';
import Present from '../Present';
import { useHelpBox } from '../HelpBox';
import { Channel } from '@n2-common/serverGeneratedTypings/channel';
import { useUiContext } from '../UiContext';

export * from './TwoColContainer';

type FormContainerProps = React.PropsWithChildren & React.HTMLAttributes<HTMLDivElement>;

export function HeaderContainer({ children, className, ...attr }: FormContainerProps): JSX.Element {
    return (
        <header className={joinNames(className, style.header)} {...attr}>
            {children}
        </header>
    );
}

export function RowWithSpaceBetweenChildren({ children, className, ...attr }: FormContainerProps): JSX.Element {
    return (
        <div className={joinNames(className, style.containerWithSpaceBetween)} {...attr}>
            {children}
        </div>
    );
}

export function PageContainer({ children, className, ...attr }: FormContainerProps): JSX.Element {
    return (
        <div className={joinNames(className, style.page)} {...attr}>
            {children}
        </div>
    );
}

export function BasicFormContainer({ children, className, ...attr }: FormContainerProps): JSX.Element {
    return (
        <div className={joinNames(className, style.basicFormContainer)} {...attr}>
            {children}
        </div>
    );
}

export function ButtonRowContainer({ children, className, ...attr }: FormContainerProps): JSX.Element {
    return (
        <div className={joinNames(className, style.buttonRowContainer)} {...attr}>
            {children}
        </div>
    );
}

export function FlexContainer({ children, className, ...attr }: FormContainerProps): JSX.Element {
    return (
        <div className={joinNames(className, style.flexContainer)} {...attr}>
            {children}
        </div>
    );
}

export function ErrorMessage({ children, className, ...attr }: FormContainerProps): JSX.Element {
    return (
        <div role="alert" className={joinNames(className, style.errorMessage)} {...attr}>
            {children}
        </div>
    );
}
export function FirstErrorMessage({ paths: pathsIn, checkTouched = true }: { paths: string[] | string; checkTouched?: boolean }) {
    const paths = Array.isArray(pathsIn) ? pathsIn : [pathsIn];
    const { errors, touched } = useFormikContext();
    const found = paths.find((path) => (!checkTouched || valueAt(touched, path)) && valueAt(errors, path));
    const message = valueAt(errors, found || '');

    return (
        <Present when={typeof message == 'string'} presenter="ExpandCollapse">
            <div role="alert" className={style.errorMessage}>
                {`${message}`}
            </div>
        </Present>
    );
}

export type ActionOptions = ActionPayload & { message: string };
export type DismissableErrorMessageProps = Omit<React.HTMLAttributes<HTMLDivElement>, 'children'> & {
    /** Error, ActionOptions or string to show  */
    error?: Error | string | ActionOptions | null;
    /** Callback function to call after message is dismissed */
    onDismiss?: () => void;
    /** Set how long time in ms the message should be shown (defaults to 30 sec) */
    timeout?: number;
};

/**
 * Component that shows an errormessage (with a dismiss button) in the bottom of the screen.
 * @param {DismissableErrorMessageProps} props
 * @returns React FC.
 */
export function DismissableErrorMessage({ className, error, onDismiss, timeout = 30000, ...attr }: DismissableErrorMessageProps): JSX.Element {
    const [state, setState] = useState<typeof error>();
    const timeoutHdl = useRef<NodeJS.Timeout>();
    const { channel } = useUiContext();
    const styleIconCloseButton = channel === Channel.MobileApp ? icons.iconCloseMobile : icons.iconClose;

    const onDismissHandler = useCallback(() => {
        setState(undefined);
        clearTimeout(timeoutHdl.current);
        if (typeof onDismiss == 'function') {
            onDismiss();
        }
    }, [onDismiss]);

    useEffect(() => {
        if (error) {
            setState(error);
            timeoutHdl.current = setTimeout(onDismissHandler, timeout);
        } else {
            setState(undefined);
        }
        () => clearTimeout(timeoutHdl.current);
    }, [error, onDismissHandler, timeout]);

    return (
        <>
            <div
                role="alert"
                className={joinNames(
                    className,
                    style.dismissableErrorMessage,
                    !state && style.dismissableErrorMessage_dismissed,
                    channel === Channel.MobileApp && style.dismissableErrorMessageMobile
                )}
                {...attr}
            >
                {errorToOutput(state, onDismissHandler)}
                <div onClick={onDismissHandler} className={joinNames(style.emoji, styleIconCloseButton)}></div>
            </div>
        </>
    );
}

function errorToOutput(state: DismissableErrorMessageProps['error'], dismissHandler: () => void) {
    switch (typeof state) {
        case 'undefined':
            return null;
        case 'string':
            return <HtmlContainer innerHtml={state} />;
        case 'object':
            if (state === null) {
                return null;
            }
            if (state instanceof Error) {
                return state.message;
            } else {
                return state.action != 'ignore' ? (
                    <div className={style.buttonTextContainer}>
                        <HtmlContainer innerHtml={state.message} />
                        {isButtonAction(state) && <ActionButton action={state} afterActionHandler={dismissHandler} uiType="small-dismissable" />}
                    </div>
                ) : null;
            }

        default:
            return `${state}`;
    }
}
/**
 *
 * @param timeout ms to automatically remove message
 * @returns Object with two properties: ErrorOutput component to insert in DOM and setErrorMessage method to show errormessage
 */

type DismissableErrorMessageStyle = 'BottomOfPage' | 'Inline';

export function useDismissableErrorMessage(timeout = 30000, uiStyle: DismissableErrorMessageStyle = 'BottomOfPage') {
    const [error, setError] = useState<DismissableErrorMessageProps['error']>();

    const comp: React.FC = () => (
        <DismissableErrorMessage
            error={error}
            className={uiStyle === 'Inline' ? style.dismissableErrorMessage_inline : ''}
            onDismiss={() => setError(undefined)}
            timeout={timeout}
        />
    );
    return { ErrorOutput: comp, setErrorMessage: setError };
}

export type HeroProps = FormContainerProps & {
    ui?: 'primary' | 'secondary' | 'ternary' | 'quaternary' | 'transparent';
};

export const Hero = React.forwardRef(function Hero(
    { children, className, ui = 'primary', ...attr }: HeroProps,
    ref?: React.ForwardedRef<HTMLDivElement>
): JSX.Element {
    return (
        <div className={joinNames(className, classFromUi(ui))} {...attr} ref={ref}>
            {children}
        </div>
    );
});

type FormElementGroupProps = { legend: string } & React.PropsWithChildren & React.HTMLAttributes<HTMLFieldSetElement>;

export const FormElementsGroup = React.forwardRef(function FormElementsGroup(
    { children, className, legend, ...attr }: FormElementGroupProps,
    ref: LegacyRef<HTMLFieldSetElement>
): JSX.Element {
    return (
        <fieldset ref={ref} className={joinNames(className, style.formGroup)} {...attr}>
            <legend>{legend}</legend>
            {children}
        </fieldset>
    );
});

type FieldSetProps = React.PropsWithChildren<{ legend: string; helptext?: string | JSX.Element | null | undefined }> &
    React.HTMLAttributes<HTMLFieldSetElement>;

/** Used as grouping of input elements of type radio or checkbox */
export function SelectionSet({ children, className, legend, helptext, ...attr }: FieldSetProps): JSX.Element {
    const [HelpBox, HelpIcon] = useHelpBox(helptext);

    return (
        <fieldset className={joinNames(className, style.selectionset)} {...attr}>
            <legend>
                <span>{legend}</span>
                <HelpIcon />
            </legend>
            <HelpBox />
            {children}
        </fieldset>
    );
}
function classFromUi(ui: Parameters<typeof Hero>[0]['ui']): string | undefined {
    switch (ui) {
        case 'primary':
            return style.primaryHero;

        case 'secondary':
            return style.secondaryHero;

        case 'ternary':
            return style.ternaryHero;

        case 'quaternary':
            return style.quaternaryHero;

        case 'transparent':
            return style.emptyHero;

        default:
            return undefined;
    }
}

type HtmlContainerProps = {
    /** Content with potentially HTML-markup is not escaped, so this control must not be used to render content from userinput. */
    innerHtml: string;
} & React.HTMLAttributes<HTMLDivElement>;

export function HtmlContainer({ innerHtml, ...props }: HtmlContainerProps) {
    return <div dangerouslySetInnerHTML={{ __html: innerHtml }} {...props}></div>;
}
